import { OrniTransportError } from "./transport.ts";
import { CloudflareNativeTerminalRelay } from "./cloudflare-native-terminal-relay.ts";
import type {
  CloudflareNativeTerminalMultiplexRelay,
  NativeTerminalMultiplexStream,
} from "./cloudflare-native-terminal-multiplex-relay.ts";
import type { NativeRelayClientDescriptor } from "./remote-terminal-ticket-client.ts";
import { randomUuid } from "./ids.ts";

export interface RemoteTerminalTarget {
  session_id: string;
  process_attempt_id: string;
  generation: number;
}

export interface RemoteTerminalControllerState {
  target: RemoteTerminalTarget;
  owner_attachment_id: string | null;
  epoch: number;
  revision: number;
  expires_at: string | null;
}

export interface RemoteTerminalSnapshotLimits {
  max_scrollback_lines: number;
  max_snapshot_bytes: number;
}

export interface RemoteTerminalCell {
  grapheme: string;
  width: number;
  attributes: number;
}

export interface RemoteTerminalLine {
  cells: RemoteTerminalCell[];
}

export interface RemoteTerminalSnapshot {
  snapshot_id: string;
  target: RemoteTerminalTarget;
  stream_id: string;
  stream_epoch: string;
  watermark: number;
  rows: number;
  columns: number;
  active_screen: "primary" | "alternate";
  modes: {
    alternate_screen: boolean;
    bracketed_paste: boolean;
    application_cursor: boolean;
    mouse_tracking: boolean;
  };
  primary: RemoteTerminalLine[];
  alternate: RemoteTerminalLine[] | null;
  scrollback: RemoteTerminalLine[];
  scrollback_text?: string;
  cursor: {
    row: number;
    column: number;
    hidden: boolean;
  };
  truncated: boolean;
  /** Exact ephemeral VT bootstrap when supplied by a current Daemon. */
  vt_bootstrap: number[];
  history?: {
    earliest_sequence: number | null;
    latest_sequence: number;
    truncated: boolean;
    has_earlier: boolean;
    frames: Array<{ sequence: number; bytes: number[] }>;
  };
}

export interface RemoteTerminalOutputFrame {
  target: RemoteTerminalTarget;
  stream_id: string;
  stream_epoch: string;
  sequence: number;
  bytes: number[];
}

export interface RemoteTerminalGap {
  target: RemoteTerminalTarget;
  requested_after_sequence: number;
  earliest_available_sequence: number;
  latest_sequence: number;
  snapshot_required: boolean;
}

/** Provider-owned prompt metadata relayed by the Daemon. Actions remain
 * opaque option ids; the control socket applies the Provider-specific bytes. */
export interface RemoteTerminalPromptOption {
  option_id: string;
  label: string;
  hotkey?: string;
  intent?: string;
}

export interface RemoteTerminalPrompt {
  interaction_id: string;
  interaction_kind?: "permission" | "confirmation" | "question";
  title: string;
  detail: string;
  options: RemoteTerminalPromptOption[];
  provider?: string;
  provider_version?: string;
  confidence?: "parsed";
  source?: "terminal_screen";
  adapter_id?: string;
  screen_watermark?: number;
}

export type RemoteTerminalObserverEvent =
  | { type: "joined"; snapshot: RemoteTerminalSnapshot; text: string }
  | { type: "controller_state"; state: RemoteTerminalControllerState }
  | { type: "pending"; sessionId: string; streamId: string; status: string }
  | { type: "pty_bound"; target: RemoteTerminalTarget }
  | { type: "pty_unbound"; target: RemoteTerminalTarget; reason: string }
  | { type: "output"; frame: RemoteTerminalOutputFrame; text: string }
  | { type: "gap"; gap: RemoteTerminalGap }
  | { type: "prompt"; prompt: RemoteTerminalPrompt }
  | { type: "prompt_resolved" }
  | { type: "closed"; reason: string };

export interface RemoteTerminalObserverOptions {
  cloudUrl: string;
  deviceId: string;
  /** Current PTY target. Omit this for a fixed-device pending attachment. */
  target?: RemoteTerminalTarget;
  pendingSessionId?: string;
  /** Legacy Cloud control ticket. Fixed-device relay attachment does not use it. */
  ticket?: string;
  /** Explicit Cloudflare relay descriptor. Absence keeps the legacy socket. */
  relay?: NativeRelayClientDescriptor;
  /** Reuse an app/device client socket when supplied by App Runtime. */
  multiplex?: CloudflareNativeTerminalMultiplexRelay;
  webSocketUrl?: string;
  snapshotLimits?: Partial<RemoteTerminalSnapshotLimits>;
  webSocketFactory?: (url: string) => WebSocket;
  /** Bounds one socket handshake; callers may obtain a fresh ticket and retry. */
  connectTimeoutMs?: number;
  /** Optional local-daemon browser authentication frame sent before the Cloud ticket. */
  authenticationFrame?: unknown;
  /** Optional bounded history projection for a one-shot history read. */
  historyAfterSequence?: number;
  historyBeforeSequence?: number;
  historyLimit?: number;
  onEvent?: (event: RemoteTerminalObserverEvent) => void;
}

const defaultLimits: RemoteTerminalSnapshotLimits = {
  max_scrollback_lines: 128,
  max_snapshot_bytes: 512 * 1024,
};

interface FanoutAssignment {
  sourceStreamId: string;
  sessionId: string;
  revision: number;
  relayUrl: string;
  clientToken: string;
  epoch: number;
  leafId: string;
}

/**
 * Observe-only browser relay. The ticket is sent once in the first frame and
 * is never written to storage, navigation state, or logs by this class.
 */
export class RemoteTerminalObserver {
  private readonly options: RemoteTerminalObserverOptions;
  private readonly events = new Set<(event: RemoteTerminalObserverEvent) => void>();
  private socket: WebSocket | null = null;
  private relay: CloudflareNativeTerminalRelay | null = null;
  private multiplexStream: NativeTerminalMultiplexStream | null = null;
  private fanoutRelay: CloudflareNativeTerminalRelay | null = null;
  private fanoutRevision = 0;
  private fanoutMigration: Promise<void> | null = null;
  private pendingFanoutAssignment: FanoutAssignment | null = null;
  private directStreamId: string | null = null;
  private disposed = false;
  private streamId: string | null = null;
  private streamEpoch: string | null = null;
  private lastSequence = 0;
  // `connect()` resolves at WebSocket open so callers can install their
  // session adapter without blocking on the first Cloud frame. A fast relay
  // can deliver `joined` (and replay frames) in that gap; retain a bounded
  // pre-subscription queue so the first snapshot can never be lost.
  private readonly pendingEvents: RemoteTerminalObserverEvent[] = [];
  private static readonly MAX_PENDING_EVENTS = 512;
  // `connect()` only waits for the WebSocket handshake.  Callers that own the
  // initial terminal acquisition need a single promise covering the protocol
  // `joined` frame as well, otherwise a projection timeout and a page-level
  // snapshot watchdog can stack into a misleading 20s+ wait.
  private readonly initialSnapshotReadyPromise: Promise<void>;
  private resolveInitialSnapshotReady!: () => void;
  private rejectInitialSnapshotReady!: (cause: Error) => void;
  private initialSnapshotReadySettled = false;
  private currentTarget: RemoteTerminalTarget | null;
  private pendingAttachmentState: boolean;
  private pendingAttachmentConfirmedState = false;
  private pendingAttachmentAcknowledgedState = false;

  constructor(options: RemoteTerminalObserverOptions) {
    this.options = options;
    this.currentTarget = options.target ?? null;
    this.pendingAttachmentState = options.pendingSessionId !== undefined;
    if (!this.currentTarget && !options.pendingSessionId) {
      throw new OrniTransportError("INVALID_ARGUMENT", "A terminal target or pending session is required.");
    }
    this.initialSnapshotReadyPromise = new Promise((resolve, reject) => {
      this.resolveInitialSnapshotReady = resolve;
      this.rejectInitialSnapshotReady = reject;
    });
    // A normal `RemoteTerminalConnection` does not await this promise (it has
    // its own persistent reconnect boundary). Mark the rejection handled here
    // so an observer that closes before joining cannot create an unhandled
    // rejection in the browser; consumers can still await the original promise.
    void this.initialSnapshotReadyPromise.catch(() => undefined);
    if (options.onEvent) this.events.add(options.onEvent);
  }

  /** Resolves once the authoritative `joined` snapshot has arrived. */
  get initialSnapshotReady(): Promise<void> { return this.initialSnapshotReadyPromise; }
  /** True while this fixed-device stream is waiting for a PTY attempt. */
  get pendingAttachment(): boolean { return this.pendingAttachmentState; }
  /** True only after the Daemon reports that no PTY is currently attachable. */
  get pendingAttachmentConfirmed(): boolean { return this.pendingAttachmentConfirmedState; }
  /** True after the Daemon has answered the session-scoped open request. */
  get pendingAttachmentAcknowledged(): boolean { return this.pendingAttachmentAcknowledgedState; }

  subscribe(listener: (event: RemoteTerminalObserverEvent) => void): () => void {
    this.events.add(listener);
    if (this.pendingEvents.length) {
      const pending = this.pendingEvents.splice(0);
      queueMicrotask(() => {
        if (this.disposed) return;
        for (const event of pending) listener(event);
      });
    }
    return () => this.events.delete(listener);
  }

  connect(): Promise<void> {
    if (this.socket) return Promise.reject(new OrniTransportError("CONFLICT", "Remote terminal is already connected."));
    if (this.relay) return Promise.reject(new OrniTransportError("CONFLICT", "Remote terminal is already connected."));
    if (this.options.relay) return this.connectRelay();
    const factory = this.options.webSocketFactory ?? ((url: string) => new WebSocket(url));
    const url = this.options.webSocketUrl ?? websocketUrl(
      this.options.cloudUrl,
      this.options.deviceId,
    );
    return new Promise((resolve, reject) => {
      let settled = false;
      const socket = factory(url);
      this.socket = socket;
      const connectTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        if (this.socket === socket) this.socket = null;
        const error = new OrniTransportError(
          "TIMEOUT",
          "Remote terminal relay handshake stalled; retrying with a fresh ticket.",
          true,
        );
        this.rejectInitialSnapshotReadyOnce(error);
        if (socket.readyState < 2) socket.close(1000, "observer handshake retry");
        reject(error);
      }, Math.max(25, this.options.connectTimeoutMs ?? 10_000));
      const clearConnectTimer = () => clearTimeout(connectTimer);
      socket.binaryType = "arraybuffer";
      socket.onopen = () => {
        clearConnectTimer();
        if (this.options.authenticationFrame !== undefined) {
          socket.send(JSON.stringify(this.options.authenticationFrame));
        }
        const limits = { ...defaultLimits, ...this.options.snapshotLimits };
        socket.send(JSON.stringify({
          ticket: this.options.ticket,
          target: this.options.target,
          after_sequence: this.options.historyAfterSequence ?? null,
          resume: null,
          ...(this.options.historyBeforeSequence === undefined
            ? {}
            : { history_before_sequence: this.options.historyBeforeSequence }),
          ...(this.options.historyLimit && this.options.historyLimit > 0
            ? { history_limit: this.options.historyLimit }
            : {}),
          snapshot_limits: limits,
        }));
        settled = true;
        resolve();
      };
      socket.onmessage = (message) => {
        void this.receive(message.data).catch((error) => {
          this.emit({ type: "closed", reason: errorText(error) });
          this.close();
        });
      };
      socket.onerror = () => {
        clearConnectTimer();
        const error = new OrniTransportError("UNAVAILABLE", "Remote terminal relay is unavailable.", true);
        this.rejectInitialSnapshotReadyOnce(error);
        if (!settled) {
          settled = true;
          reject(error);
        }
        this.emit({ type: "closed", reason: error.message });
      };
      socket.onclose = (event) => {
        clearConnectTimer();
        this.socket = null;
        this.rejectInitialSnapshotReadyOnce(new OrniTransportError(
          "UNAVAILABLE",
          event.reason || "Remote terminal relay closed before joining.",
          true,
        ));
        if (!settled) {
          settled = true;
          reject(new OrniTransportError("UNAVAILABLE", "Remote terminal relay closed before joining.", true));
        }
        if (!this.disposed) this.emit({ type: "closed", reason: event.reason || "Remote terminal relay closed." });
      };
    });
  }

  close(): void {
    this.rejectInitialSnapshotReadyOnce(new OrniTransportError(
      "UNAVAILABLE",
      "Remote terminal relay closed before joining.",
      true,
    ));
    this.disposed = true;
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState < 2) socket.close(1000, "observer closed");
    const relay = this.relay;
    this.relay = null;
    relay?.close(1000, "observer closed");
    const fanoutRelay = this.fanoutRelay;
    this.fanoutRelay = null;
    fanoutRelay?.close(1000, "observer closed");
    this.multiplexStream?.close("observer closed");
    this.multiplexStream = null;
  }

  private async connectRelay(): Promise<void> {
    const descriptor = this.options.relay;
    if (!descriptor) throw new OrniTransportError("CONTRACT_INVALID", "Cloudflare relay descriptor is missing.");
    const streamId = this.options.pendingSessionId
      ? `pending:${this.options.pendingSessionId}:${randomUuid()}`
      : `${this.options.target!.session_id}:${this.options.target!.process_attempt_id}:${this.options.target!.generation}`;
    this.directStreamId = streamId;
    if (this.options.multiplex) {
      const stream = await this.options.multiplex.openStream(
        descriptor,
        streamId,
        (message) => {
          if (message.type === "frame") {
            void this.receiveRelayBody(message.body).catch((error) => {
              this.emit({ type: "closed", reason: errorText(error) });
              this.close();
            });
          } else if (message.type === "close" || message.type === "gap") {
            this.rejectInitialSnapshotReadyOnce(new OrniTransportError(
              "UNAVAILABLE",
              message.type === "close" ? message.reason : "Remote terminal stream gap.",
              true,
            ));
            this.emit({ type: "closed", reason: message.type === "close" ? message.reason : "Remote terminal stream gap." });
          }
        },
      );
      this.multiplexStream = stream;
      if (this.options.pendingSessionId) {
        stream.sendFrame({ type: "open_session", request_id: randomUuid(), request: {
          session_id: this.options.pendingSessionId,
          stream_id: streamId,
          snapshot_limits: { ...defaultLimits, ...this.options.snapshotLimits },
        } });
      } else {
        stream.sendFrame({ type: "snapshot_request", request_id: randomUuid(), request: {
          target: this.options.target,
          after_sequence: this.options.historyAfterSequence ?? null,
          resume: null,
          ...(this.options.historyBeforeSequence === undefined ? {} : { history_before_sequence: this.options.historyBeforeSequence }),
          ...(this.options.historyLimit && this.options.historyLimit > 0 ? { history_limit: this.options.historyLimit } : {}),
          snapshot_limits: { ...defaultLimits, ...this.options.snapshotLimits },
        } });
      }
      return;
    }
    const relay = new CloudflareNativeTerminalRelay({
      relayUrl: descriptor.relayUrl,
      role: "client",
      token: descriptor.clientToken,
      epoch: descriptor.epoch,
      streamId,
      webSocketFactory: this.options.webSocketFactory,
      notifyClose: true,
      onMessage: (message) => {
        if (message.type === "frame") {
          void this.receiveRelayBody(message.body).catch((error) => {
            this.emit({ type: "closed", reason: errorText(error) });
            this.close();
          });
        } else if (message.type === "close") {
          this.rejectInitialSnapshotReadyOnce(new OrniTransportError("UNAVAILABLE", message.reason, true));
          this.emit({ type: "closed", reason: message.reason });
        }
      },
      connectTimeoutMs: this.options.connectTimeoutMs,
    });
    this.relay = relay;
    try {
      await relay.connect();
      if (this.options.pendingSessionId) {
        relay.sendFrame({ type: "open_session", request_id: randomUuid(), request: {
          session_id: this.options.pendingSessionId,
          stream_id: streamId,
          snapshot_limits: { ...defaultLimits, ...this.options.snapshotLimits },
        } });
      } else {
        relay.sendFrame({ type: "snapshot_request", request_id: randomUuid(), request: {
          target: this.currentTarget ?? this.options.target!,
          after_sequence: this.options.historyAfterSequence ?? null,
          resume: null,
          ...(this.options.historyBeforeSequence === undefined ? {} : { history_before_sequence: this.options.historyBeforeSequence }),
          ...(this.options.historyLimit && this.options.historyLimit > 0 ? { history_limit: this.options.historyLimit } : {}),
          snapshot_limits: { ...defaultLimits, ...this.options.snapshotLimits },
        } });
      }
    } catch (error) {
      relay.close();
      this.relay = null;
      this.rejectInitialSnapshotReadyOnce(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  private async receiveRelayBody(body: Record<string, unknown>): Promise<void> {
    await this.receive(JSON.stringify(body));
  }

  private sendDirectFanoutControl(
    type: "fanout_commit" | "fanout_resume_direct" | "fanout_release",
    assignment: FanoutAssignment,
  ): void {
    const body = { type, leaf_id: assignment.leafId, revision: assignment.revision };
    if (this.multiplexStream) this.multiplexStream.sendFrame(body);
    else this.relay?.sendFrame(body);
  }

  private beginFanoutMigration(value: Record<string, unknown>): void {
    let assignment: FanoutAssignment;
    try {
      assignment = parseFanoutAssignment(
        value,
        this.options.pendingSessionId ?? this.currentTarget?.session_id,
        this.options.relay?.relayUrl,
      );
    } catch {
      return;
    }
    if (assignment.revision <= this.fanoutRevision || this.fanoutMigration || this.fanoutRelay) return;
    if (!this.currentTarget || !this.directStreamId) {
      if (!this.pendingFanoutAssignment || assignment.revision > this.pendingFanoutAssignment.revision) {
        this.pendingFanoutAssignment = assignment;
      }
      return;
    }
    const expectedTarget = { ...this.currentTarget };
    this.fanoutMigration = this.migrateToFanout(assignment, expectedTarget)
      .catch(() => undefined)
      .finally(() => { this.fanoutMigration = null; });
  }

  private async migrateToFanout(assignment: FanoutAssignment, expectedTarget: RemoteTerminalTarget): Promise<void> {
    let committed = false;
    const relayHolder: { current: CloudflareNativeTerminalRelay | null } = { current: null };
    const pendingBodies: Record<string, unknown>[] = [];
    const pendingData: Array<{ sequence: number; bytes: Uint8Array }> = [];
    try {
      const requestId = randomUuid();
      const snapshot = await new Promise<RemoteTerminalSnapshot>(async (resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new OrniTransportError("TIMEOUT", "Fan-out snapshot timed out.", true));
        }, Math.max(25, this.options.connectTimeoutMs ?? 10_000));
        const finish = (result: () => void): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          result();
        };
        const relay = new CloudflareNativeTerminalRelay({
          relayUrl: assignment.relayUrl,
          role: "client",
          token: assignment.clientToken,
          epoch: assignment.epoch,
          streamId: assignment.sourceStreamId,
          webSocketFactory: this.options.webSocketFactory,
          connectTimeoutMs: this.options.connectTimeoutMs,
          notifyClose: true,
          onMessage: (message) => {
            if (message.type === "frame") {
              if (!committed) {
                if (message.body.type !== "snapshot" || message.body.request_id !== requestId) {
                  if (pendingBodies.length + pendingData.length >= RemoteTerminalObserver.MAX_PENDING_EVENTS) {
                    finish(() => reject(new OrniTransportError("UNAVAILABLE", "Fan-out migration overlap exceeded its buffer.", true)));
                    return;
                  }
                  pendingBodies.push(message.body);
                  return;
                }
                try {
                  const parsed = parseSnapshot(message.body.snapshot);
                  ensureTarget(parsed.target, expectedTarget);
                  finish(() => resolve(parsed));
                } catch (cause) {
                  finish(() => reject(cause));
                }
                return;
              }
              void this.receiveRelayBody(message.body).catch((error) => {
                this.emit({ type: "closed", reason: errorText(error) });
                this.close();
              });
            } else if (message.type === "data") {
              if (committed) this.acceptFanoutData(message.sequence, message.bytes);
              else {
                if (pendingBodies.length + pendingData.length >= RemoteTerminalObserver.MAX_PENDING_EVENTS) {
                  finish(() => reject(new OrniTransportError("UNAVAILABLE", "Fan-out migration overlap exceeded its buffer.", true)));
                  return;
                }
                pendingData.push({ sequence: message.sequence, bytes: message.bytes });
              }
            } else if (message.type === "close" || message.type === "gap") {
              const error = new OrniTransportError(
                "UNAVAILABLE",
                message.type === "close" ? message.reason : "Fan-out relay stream gap.",
                true,
              );
              if (!committed) finish(() => reject(error));
              else {
                this.emit({ type: "closed", reason: error.message });
                this.close();
              }
            }
          },
        });
        relayHolder.current = relay;
        try {
          await relay.connect();
          relay.sendFrame({
            type: "snapshot_request",
            request_id: requestId,
            request: {
              target: expectedTarget,
              after_sequence: null,
              resume: null,
              snapshot_limits: { ...defaultLimits, ...this.options.snapshotLimits },
              history_before_sequence: null,
              history_limit: 0,
            },
          });
        } catch (cause) {
          finish(() => reject(cause));
        }
      });
      if (this.disposed || !this.currentTarget) throw new OrniTransportError("UNAVAILABLE", "Observer closed during fan-out migration.", true);
      ensureTarget(this.currentTarget, expectedTarget);
      if (snapshot.watermark < this.lastSequence) {
        throw new OrniTransportError("CONTRACT_INVALID", "Fan-out snapshot is older than the direct stream.");
      }
      this.sendDirectFanoutControl("fanout_commit", assignment);
      committed = true;
      this.fanoutRevision = assignment.revision;
      this.fanoutRelay = relayHolder.current;
      this.multiplexStream?.close("fanout committed");
      this.multiplexStream = null;
      const directRelay = this.relay;
      this.relay = null;
      directRelay?.close(1000, "fanout committed");
      this.applySnapshot(snapshot);
      for (const body of pendingBodies) await this.receiveRelayBody(body);
      for (const data of pendingData) this.acceptFanoutData(data.sequence, data.bytes);
    } catch (cause) {
      relayHolder.current?.close(1000, "fanout fallback");
      if (!committed) {
        try { this.sendDirectFanoutControl("fanout_resume_direct", assignment); } catch {}
      } else {
        this.emit({ type: "closed", reason: errorText(cause) });
        this.close();
      }
      throw cause;
    }
  }

  private applySnapshot(snapshot: RemoteTerminalSnapshot): void {
    if (this.options.pendingSessionId && snapshot.target.session_id !== this.options.pendingSessionId) {
      throw new OrniTransportError("CONTRACT_INVALID", "Pending terminal snapshot belongs to a different Session.");
    }
    if (this.currentTarget) ensureTarget(snapshot.target, this.currentTarget);
    this.currentTarget = snapshot.target;
    this.pendingAttachmentState = false;
    this.pendingAttachmentConfirmedState = false;
    this.pendingAttachmentAcknowledgedState = true;
    this.streamId = snapshot.stream_id;
    this.streamEpoch = snapshot.stream_epoch;
    this.lastSequence = snapshot.watermark;
    this.resolveInitialSnapshotReadyOnce();
    this.emit({ type: "joined", snapshot, text: snapshotText(snapshot) });
    const pendingAssignment = this.pendingFanoutAssignment;
    this.pendingFanoutAssignment = null;
    if (pendingAssignment && !this.fanoutMigration && !this.fanoutRelay) {
      this.beginFanoutMigration({
        type: "fanout_assignment",
        source_stream_id: pendingAssignment.sourceStreamId,
        session_id: pendingAssignment.sessionId,
        revision: pendingAssignment.revision,
        relay_url: pendingAssignment.relayUrl,
        client_token: pendingAssignment.clientToken,
        epoch: pendingAssignment.epoch,
        leaf_id: pendingAssignment.leafId,
      });
    }
  }

  private acceptFanoutData(sequence: number, bytes: Uint8Array): void {
    if (!this.currentTarget || !this.streamId || !this.streamEpoch) return;
    this.acceptOutput({
      target: this.currentTarget,
      stream_id: this.streamId,
      stream_epoch: this.streamEpoch,
      sequence,
      bytes: Array.from(bytes),
    });
  }

  private async receive(data: unknown): Promise<void> {
    const encoded = typeof data === "string"
      ? data
      : data instanceof ArrayBuffer
        ? new TextDecoder().decode(new Uint8Array(data))
        : data instanceof Blob
          ? await data.text()
          : null;
    if (encoded === null) throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal sent an unsupported frame.");
    let decoded: unknown;
    try {
      decoded = JSON.parse(encoded);
    } catch {
      throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned invalid JSON.");
    }
    const value = parseRecord(decoded);
    switch (value.type) {
      case "snapshot": {
        // The Cloudflare transport connects the browser directly to the
        // Daemon source and therefore receives the device protocol's raw
        // snapshot response. The legacy Cloud relay wraps the same snapshot
        // in `joined`. Normalize both forms here so presentation and terminal
        // lifecycle code remain transport-independent.
        const snapshot = parseSnapshot(value.snapshot);
        this.applySnapshot(snapshot);
        break;
      }
      case "joined": {
        const join = parseRecord(value.join);
        const snapshot = parseSnapshot(join.snapshot);
        if (this.options.pendingSessionId && snapshot.target.session_id !== this.options.pendingSessionId) {
          throw new OrniTransportError("CONTRACT_INVALID", "Pending terminal snapshot belongs to a different Session.");
        }
        if (this.currentTarget) ensureTarget(snapshot.target, this.currentTarget);
        this.currentTarget = snapshot.target;
        this.pendingAttachmentState = false;
        this.pendingAttachmentConfirmedState = false;
        this.pendingAttachmentAcknowledgedState = true;
        this.streamId = text(join.stream_id);
        this.streamEpoch = text(join.stream_epoch);
        if (snapshot.stream_id !== this.streamId || snapshot.stream_epoch !== this.streamEpoch) {
          throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal join stream does not match its snapshot.");
        }
        this.lastSequence = snapshot.watermark;
        this.resolveInitialSnapshotReadyOnce();
        this.emit({ type: "joined", snapshot, text: snapshotText(snapshot) });
        const replay = Array.isArray(join.replay) ? join.replay.map(parseOutput) : [];
        for (const frame of replay) {
          this.acceptOutput(frame);
        }
        break;
      }
      case "output": {
        const frame = parseOutput(value.frame);
        this.acceptOutput(frame);
        break;
      }
      case "controller_state": {
        const state = parseControllerState(value.state);
        if (this.currentTarget) ensureTarget(state.target, this.currentTarget);
        this.emit({ type: "controller_state", state });
        break;
      }
      case "stream_pending": {
        const status = text(value.status);
        const sessionId = text(value.session_id);
        if (this.options.pendingSessionId && sessionId !== this.options.pendingSessionId) {
          throw new OrniTransportError("CONTRACT_INVALID", "Pending terminal response belongs to a different Session.");
        }
        this.pendingAttachmentState = status === "waiting_for_pty" || status === "starting";
        this.pendingAttachmentConfirmedState = status === "waiting_for_pty";
        this.pendingAttachmentAcknowledgedState = true;
        this.emit({ type: "pending", sessionId, streamId: text(value.stream_id), status });
        if (!this.pendingAttachmentState) {
          const error = new OrniTransportError("UNAVAILABLE", `Remote terminal attachment rejected: ${status}.`);
          this.rejectInitialSnapshotReadyOnce(error);
          this.emit({ type: "closed", reason: error.message });
        }
        break;
      }
      case "pty_bound": {
        const target = parseTarget(value.target);
        if (this.options.pendingSessionId && target.session_id !== this.options.pendingSessionId) {
          throw new OrniTransportError("CONTRACT_INVALID", "Pending terminal binding belongs to a different Session.");
        }
        this.pendingAttachmentState = false;
        this.pendingAttachmentConfirmedState = false;
        this.pendingAttachmentAcknowledgedState = true;
        this.currentTarget = target;
        this.emit({ type: "pty_bound", target });
        break;
      }
      case "pty_unbound": {
        const target = parseTarget(value.target);
        if (this.options.pendingSessionId && target.session_id !== this.options.pendingSessionId) {
          throw new OrniTransportError("CONTRACT_INVALID", "Pending terminal unbind belongs to a different Session.");
        }
        this.pendingAttachmentState = true;
        this.pendingAttachmentConfirmedState = false;
        this.pendingAttachmentAcknowledgedState = true;
        this.currentTarget = null;
        this.emit({ type: "pty_unbound", target, reason: text(value.reason) });
        break;
      }
      case "gap": {
        const gap = parseGap(value.gap);
        this.emit({ type: "gap", gap });
        break;
      }
      case "terminal_prompt": {
        const prompt = parsePrompt(value.prompt ?? value);
        this.emit({ type: "prompt", prompt });
        break;
      }
      case "terminal_prompt_resolved":
        this.emit({ type: "prompt_resolved" });
        break;
      case "control_result":
      case "upload_result":
      case "lease":
      case "control_ready":
      case "control_error":
        // Cloudflare multiplexes observe and control attachments over the
        // same target stream. These acknowledgements belong to the control
        // adapter and must not tear down an otherwise healthy observer.
        break;
      case "fanout_assignment":
        this.beginFanoutMigration(value);
        break;
      case "closed":
        this.rejectInitialSnapshotReadyOnce(new OrniTransportError(
          "UNAVAILABLE",
          typeof value.reason === "string" ? value.reason : "Remote terminal relay closed before joining.",
          true,
        ));
        this.emit({ type: "closed", reason: typeof value.reason === "string" ? value.reason : "Remote terminal relay closed." });
        // A protocol close is terminal for this socket, but not for the
        // session. Closing the underlying socket releases the relay attachment
        // before RemoteTerminalConnection obtains a fresh ticket.
        this.socket?.close(1011, "remote relay closed");
        break;
      default:
        throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an unknown message.");
    }
  }

  private resolveInitialSnapshotReadyOnce(): void {
    if (this.initialSnapshotReadySettled) return;
    this.initialSnapshotReadySettled = true;
    this.resolveInitialSnapshotReady();
  }

  private rejectInitialSnapshotReadyOnce(cause: Error): void {
    if (this.initialSnapshotReadySettled) return;
    this.initialSnapshotReadySettled = true;
    this.rejectInitialSnapshotReady(cause);
  }

  private emit(event: RemoteTerminalObserverEvent): void {
    if (!this.events.size && !this.disposed) {
      if (this.pendingEvents.length >= RemoteTerminalObserver.MAX_PENDING_EVENTS) {
        this.pendingEvents.shift();
      }
      this.pendingEvents.push(event);
    }
    for (const listener of this.events) listener(event);
  }

  private acceptOutput(frame: RemoteTerminalOutputFrame): void {
    if (this.currentTarget) ensureTarget(frame.target, this.currentTarget);
    this.currentTarget = frame.target;
    if (frame.stream_id !== this.streamId || frame.stream_epoch !== this.streamEpoch) {
      throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal output crossed stream boundaries.");
    }
    if (frame.sequence <= this.lastSequence) return;
    if (frame.sequence !== this.lastSequence + 1) {
      throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal output contains a sequence gap.");
    }
    this.lastSequence = frame.sequence;
    this.emit({ type: "output", frame, text: decodeBytes(frame.bytes) });
  }
}

function websocketUrl(cloudUrl: string, deviceId: string): string {
  const baseUrl = cloudUrl || globalThis.location?.origin;
  if (!baseUrl) throw new OrniTransportError("INVALID_ARGUMENT", "A Cloud origin is required for remote terminal observation.");
  const url = new URL(`/v1/devices/${encodeURIComponent(deviceId)}/remote-terminal/observe`, baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function snapshotText(snapshot: RemoteTerminalSnapshot): string {
  const screen = snapshot.active_screen === "alternate"
    ? snapshot.alternate ?? []
    : snapshot.primary;
  return screen.map((line) => line.cells.map((cell) => cell.grapheme).join("")).join("\n");
}

function decodeBytes(bytes: number[]): string {
  return new TextDecoder().decode(Uint8Array.from(bytes));
}

function parseRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid message.");
  }
  return value as Record<string, unknown>;
}

function parseFanoutAssignment(
  value: Record<string, unknown>,
  expectedSessionId?: string,
  directRelayUrl?: string,
): FanoutAssignment {
  const sourceStreamId = text(value.source_stream_id);
  const sessionId = text(value.session_id);
  const revision = positiveInteger(value.revision);
  const relayUrl = text(value.relay_url);
  const clientToken = text(value.client_token);
  const epoch = positiveInteger(value.epoch);
  const leafId = text(value.leaf_id);
  let url: URL;
  let directUrl: URL | null = null;
  try { url = new URL(relayUrl); } catch {
    throw new OrniTransportError("CONTRACT_INVALID", "Fan-out relay URL is invalid.");
  }
  try { directUrl = directRelayUrl ? new URL(directRelayUrl) : null; } catch {
    throw new OrniTransportError("CONTRACT_INVALID", "Direct relay URL is invalid.");
  }
  if (value.type !== "fanout_assignment"
    || sourceStreamId.length === 0
    || sourceStreamId.length > 160
    || (expectedSessionId && sessionId !== expectedSessionId)
    || url.protocol !== "wss:"
    || (directUrl !== null && url.hostname !== directUrl.hostname)
    || url.pathname !== "/v1/socket"
    || url.searchParams.get("leaf") !== leafId
    || Array.from(url.searchParams.keys()).some((key) => key !== "leaf")
    || !/^[a-z0-9]{24}$/.test(leafId)
    || clientToken.length < 32) {
    throw new OrniTransportError("CONTRACT_INVALID", "Fan-out assignment is invalid.");
  }
  return { sourceStreamId, sessionId, revision, relayUrl, clientToken, epoch, leafId };
}

function parseSnapshot(value: unknown): RemoteTerminalSnapshot {
  const row = parseRecord(value);
  const target = parseTarget(row.target);
  const primary = Array.isArray(row.primary) ? row.primary.map(parseLine) : [];
  const alternate = row.alternate === null || row.alternate === undefined
    ? null
    : Array.isArray(row.alternate)
      ? row.alternate.map(parseLine)
      : (() => { throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid alternate screen."); })();
  const activeScreen = row.active_screen === "alternate" ? "alternate" : "primary";
  if (activeScreen === "alternate" && !alternate) {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal alternate screen is missing.");
  }
  const cursor = parseRecord(row.cursor);
  return {
    snapshot_id: text(row.snapshot_id),
    target,
    stream_id: text(row.stream_id),
    stream_epoch: text(row.stream_epoch),
    // A newly attached terminal can have no output yet. Zero is a valid
    // watermark; subsequent output frames still start at sequence one.
    watermark: positiveInteger(row.watermark, true),
    rows: positiveInteger(row.rows),
    columns: positiveInteger(row.columns),
    active_screen: activeScreen,
    modes: (() => {
      // `modes` was added after the first remote-terminal snapshot schema.
      // Keep old Daemons readable while defaulting every mode to the
      // protocol-safe value instead of trusting an unchecked object cast.
      const modes = row.modes && typeof row.modes === "object" && !Array.isArray(row.modes)
        ? row.modes as Record<string, unknown>
        : {};
      return {
        alternate_screen: modes.alternate_screen === true,
        bracketed_paste: modes.bracketed_paste === true,
        application_cursor: modes.application_cursor === true,
        mouse_tracking: modes.mouse_tracking === true,
      };
    })(),
    primary,
    alternate,
    scrollback: Array.isArray(row.scrollback) ? row.scrollback.map(parseLine) : [],
    scrollback_text: typeof row.scrollback_text === "string" ? row.scrollback_text : undefined,
    cursor: {
      row: positiveInteger(cursor.row, true),
      column: positiveInteger(cursor.column, true),
      hidden: cursor.hidden === true,
    },
    truncated: row.truncated === true,
    vt_bootstrap: Array.isArray(row.vt_bootstrap) ? row.vt_bootstrap.map(byte) : [],
    history: row.history && typeof row.history === "object" && !Array.isArray(row.history)
      ? parseHistory(row.history)
      : undefined,
  };
}

function parseHistory(value: unknown): RemoteTerminalSnapshot["history"] {
  const row = parseRecord(value);
  const frames = Array.isArray(row.frames)
    ? row.frames.map((item) => {
      const frame = parseRecord(item);
      return {
        sequence: positiveInteger(frame.sequence),
        bytes: Array.isArray(frame.bytes) ? frame.bytes.map(byte) : [],
      };
    })
    : [];
  return {
    earliest_sequence: row.earliest_sequence === null || row.earliest_sequence === undefined
      ? null
      : positiveInteger(row.earliest_sequence),
    latest_sequence: positiveInteger(row.latest_sequence, true),
    truncated: row.truncated === true,
    has_earlier: row.has_earlier === true,
    frames,
  };
}

function parseOutput(value: unknown): RemoteTerminalOutputFrame {
  const row = parseRecord(value);
  const bytes = Array.isArray(row.bytes) ? row.bytes.map(byte) : [];
  return {
    target: parseTarget(row.target),
    stream_id: text(row.stream_id),
    stream_epoch: text(row.stream_epoch),
    sequence: positiveInteger(row.sequence),
    bytes,
  };
}

function ensureTarget(actual: RemoteTerminalTarget, expected: RemoteTerminalTarget): void {
  if (actual.session_id !== expected.session_id
    || actual.process_attempt_id !== expected.process_attempt_id
    || actual.generation !== expected.generation) {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal crossed Session target boundaries.");
  }
}

function parseGap(value: unknown): RemoteTerminalGap {
  const row = parseRecord(value);
  return {
    target: parseTarget(row.target),
    requested_after_sequence: positiveInteger(row.requested_after_sequence, true),
    earliest_available_sequence: positiveInteger(row.earliest_available_sequence),
    latest_sequence: positiveInteger(row.latest_sequence),
    snapshot_required: row.snapshot_required === true,
  };
}

function parsePrompt(value: unknown): RemoteTerminalPrompt {
  const row = parseRecord(value);
  const interactionId = text(row.interaction_id ?? row.prompt_id ?? row.id);
  const title = text(row.title);
  const detail = typeof row.detail === "string" ? row.detail : "";
  const rawOptions = Array.isArray(row.options) ? row.options : [];
  const options = rawOptions.flatMap((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return [];
    const option = item as Record<string, unknown>;
    const optionId = text(option.option_id ?? option.id);
    const label = text(option.label);
    if (!optionId || !label) return [];
    return [{
      option_id: optionId,
      label,
      ...(typeof option.hotkey === "string" && option.hotkey ? { hotkey: option.hotkey } : {}),
      ...(typeof option.intent === "string" && option.intent ? { intent: option.intent } : {}),
    }];
  });
  if (!interactionId || !title || options.length === 0) {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid prompt.");
  }
  const kind = row.interaction_kind ?? row.kind;
  const provider = row.provider;
  const confidence = row.confidence;
  const source = row.source;
  return {
    interaction_id: interactionId,
    ...(kind === "permission" || kind === "confirmation" || kind === "question" ? { interaction_kind: kind } : {}),
    title,
    detail,
    options,
    ...(typeof provider === "string" && provider ? { provider } : {}),
    ...(typeof row.provider_version === "string" && row.provider_version ? { provider_version: row.provider_version } : {}),
    ...(confidence === "parsed" ? { confidence } : {}),
    ...(source === "terminal_screen" ? { source } : {}),
    ...(typeof row.adapter_id === "string" && row.adapter_id ? { adapter_id: row.adapter_id } : {}),
    ...(typeof row.screen_watermark === "number" ? { screen_watermark: row.screen_watermark } : {}),
  };
}

function parseTarget(value: unknown): RemoteTerminalTarget {
  const row = parseRecord(value);
  return {
    session_id: text(row.session_id),
    process_attempt_id: text(row.process_attempt_id),
    generation: positiveInteger(row.generation),
  };
}

function parseControllerState(value: unknown): RemoteTerminalControllerState {
  const row = parseRecord(value);
  const epoch = positiveInteger(row.epoch, true);
  const revision = positiveInteger(row.revision, true);
  const owner = row.owner_attachment_id;
  if (owner !== null && typeof owner !== "string") {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid controller owner.");
  }
  const expires = row.expires_at;
  if (expires !== null && typeof expires !== "string") {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid controller expiry.");
  }
  return {
    target: parseTarget(row.target),
    owner_attachment_id: owner as string | null,
    epoch,
    revision,
    expires_at: expires as string | null,
  };
}

function parseLine(value: unknown): RemoteTerminalLine {
  const row = parseRecord(value);
  return {
    cells: Array.isArray(row.cells) ? row.cells.map((cell: unknown) => {
      const value = parseRecord(cell);
      return {
        grapheme: text(value.grapheme),
        width: positiveInteger(value.width, true),
        attributes: positiveInteger(value.attributes, true),
      };
    }) : [],
  };
}

function text(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned invalid text.");
  return value;
}

function positiveInteger(value: unknown, allowZero = false): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || (allowZero ? value < 0 : value < 1)) {
    throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid number.");
  }
  return value;
}

function byte(value: unknown): number {
  const result = positiveInteger(value, true);
  if (result > 255) throw new OrniTransportError("CONTRACT_INVALID", "Remote terminal returned an invalid byte.");
  return result;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Remote terminal relay failed.";
}
